import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageCircle, Edit3, Trash2, ChevronDown } from 'lucide-react';
import { useAuthStore } from '@/features/auth';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { UserAvatar } from '@/components/ui';
import type { PostComment } from '../types';

function formatTimeAgo(isoString: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const now = Date.now();
  const then = new Date(isoString).getTime();
  const diffSeconds = Math.floor((now - then) / 1000);

  if (diffSeconds < 60) return t('feed.time_just_now');
  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) return t('feed.time_minutes_ago', { count: diffMinutes });
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return t('feed.time_hours_ago', { count: diffHours });
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) return t('feed.time_days_ago', { count: diffDays });
  const diffMonths = Math.floor(diffDays / 30);
  return t('feed.time_months_ago', { count: diffMonths });
}

interface CommentItemProps {
  comment: PostComment;
  onReply: (comment: PostComment) => void;
  onEdit: (comment: PostComment) => void;
  onDelete: (commentId: string) => void;
  onShowReplies?: (commentId: string) => void;
  depth?: number;
}

export function CommentItem({
  comment,
  onReply,
  onEdit,
  onDelete,
  onShowReplies,
  depth = 0,
}: CommentItemProps) {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const { isAdmin } = usePermissions();
  const [showReplies, setShowReplies] = useState(false);

  const isAuthor = user?.id === comment.author.id;
  const canManage = isAuthor || isAdmin();

  const maxIndent = 3;
  const indentLevel = Math.min(depth, maxIndent);

  return (
    <div
      style={{
        paddingInlineStart: indentLevel > 0 ? `${indentLevel * 16}px` : undefined,
      }}
    >
      <div
        className="glass-tinted flex gap-2.5 py-2.5"
        style={{
          borderInlineStart:
            depth > 0 ? '2px solid #f1f5f9' : undefined,
          paddingInlineStart: depth > 0 ? '12px' : undefined,
        }}
      >
        {/* Avatar */}
        <UserAvatar
          src={comment.author.avatarUrl}
          name={`${comment.author.firstName ?? ''} ${comment.author.lastName ?? ''}`}
          size="sm"
          className="border border-[var(--user-page-card-border)]"
        />

        <div className="flex-1 min-w-0">
          {/* Author name + time */}
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-xs font-medium truncate" style={{ color: 'var(--user-page-heading)' }}>
              {comment.author.firstName} {comment.author.lastName}
            </span>
            <span className="text-[10px]" style={{ color: 'var(--user-page-text)' }}>
              {formatTimeAgo(comment.createdAt, t)}
            </span>
            {comment.isEdited && (
              <span className="text-[10px] italic" style={{ color: 'var(--user-page-text)' }}>
                ({t('feed.edited')})
              </span>
            )}
          </div>

          {/* Comment text */}
          <p
            className="text-sm font-medium leading-relaxed whitespace-pre-wrap break-words"
            style={{ color: 'var(--user-page-heading)' }}
            dir="auto"
          >
            {comment.content}
          </p>

          {/* Actions */}
          <div className="flex items-center gap-3 mt-1.5">
            <button
              onClick={() => onReply(comment)}
              className="flex items-center gap-1 text-[11px] font-semibold transition-colors hover:opacity-80"
              style={{ color: 'var(--color-primary-500)' }}
            >
              <MessageCircle className="h-3 w-3" />
              {t('feed.reply')}
            </button>
            {canManage && (
              <>
                <button
                  onClick={() => onEdit(comment)}
                  className="flex items-center gap-1 text-[11px] font-medium transition-colors hover:opacity-80"
                  style={{ color: 'var(--user-page-text)' }}
                >
                  <Edit3 className="h-3 w-3" />
                  {t('feed.edit')}
                </button>
                <button
                  onClick={() => onDelete(comment.id)}
                  className="flex items-center gap-1 text-[11px] font-bold transition-colors hover:opacity-80"
                  style={{ color: '#ef4444' }}
                >
                  <Trash2 className="h-3 w-3" />
                  {t('feed.delete')}
                </button>
              </>
            )}
          </div>

          {/* Show more replies button */}
          {(comment.replyCount ?? 0) > 0 && !showReplies && depth === 0 && (
            <button
              onClick={() => {
                setShowReplies(true);
                onShowReplies?.(comment.id);
              }}
              className="flex items-center gap-1 mt-2 text-[11px] font-bold transition-colors hover:opacity-80"
              style={{ color: 'var(--color-primary-500)' }}
            >
              <ChevronDown className="h-3 w-3" />
              {t('feed.show_replies', { count: comment.replyCount })}
            </button>
          )}

          {/* Nested replies */}
          {showReplies && comment.replies && comment.replies.length > 0 && (
            <div className="mt-2">
              {comment.replies.map((reply) => (
                <CommentItem
                  key={reply.id}
                  comment={reply}
                  onReply={onReply}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  depth={depth + 1}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
